import * as React from 'react';
import { isArray, isObject } from 'lodash';

import {
  GetSuggestionItemProps,
  SuggestionItemComputedProps,
  Value,
} from './types';
import { checkIsTheSameObject } from '../../utils';
import {
  selectAllSuggestion,
  SelectedState,
} from '../../components/MultiSelect/constants';

/**
 * Helper gets name of the list item
 * @param {Value} suggestion - Item of the list
 * @param {string} textField - Required if suggestion is an object
 *
 * @returns {string} Name of the list item
 */
export const getText = (suggestion?: Value, textField?: string): string => {
  if (!suggestion) return ''; // null or undefined

  if (!textField) return suggestion?.toString() || '';
  return isObject(suggestion)
    // @ts-ignore
    ? ((suggestion[textField] as string | number | undefined) || '').toString()
    : suggestion.toString();
};

/**
 * Helper scrolls to selected item
 * @param {React.MutableRefObject<HTMLElement | null>} containerRef - Ref of the container
 * @param {React.MutableRefObject<HTMLElement | null>} suggestionRef - Ref of the item
 *
 * @returns {void}
 */
export const scrollToSuggestion = (
  containerRef: React.MutableRefObject<HTMLElement | null>,
  suggestionRef: React.MutableRefObject<HTMLElement | null>
): void => {
  if (!containerRef.current || !suggestionRef.current) return;

  const suggestionRect = suggestionRef.current.getBoundingClientRect();

  const containerRect = containerRef.current.getBoundingClientRect();

  const padding = 2;

  const offset = ((): number => {
    if (suggestionRect.top < containerRect.top) {
      return suggestionRect.top - containerRect.top - padding;
    }
    if (containerRect.bottom < suggestionRect.bottom) {
      return suggestionRect.bottom - containerRect.bottom + padding;
    }
    return 0;
  })();

  if (offset) containerRef.current.scrollBy?.(0, offset);
};

/**
 * Helper computes additional properties of SuggestionItem component
 * @param {SuggestionItemComputedProps} - Additional properties of SuggestionItem component
 *
 * @returns {SuggestionItemComputedProps} - Additional properties
 */
export const getSuggestionItemProps = ({
  compareObjectsBy,
  suggestion,
  textField,
  placeholder,
  highlightedSuggestion,
  selectedSuggestion,
  selectAllState,
}: GetSuggestionItemProps): SuggestionItemComputedProps => {
  const text = getText(suggestion, textField);

  const isSelectAllItem = suggestion === selectAllSuggestion;

  const isPlaceholder = text === placeholder;
  const isHighlighted = checkIsTheSameObject({
    compareObjectsBy,
    obj1: suggestion,
    obj2: highlightedSuggestion,
  });
  const isSelected = ((): boolean => {
    if (isSelectAllItem) {
      return selectAllState === SelectedState.All;
    }

    // MultiSelect mode
    if (isArray(selectedSuggestion)) {
      return selectedSuggestion.some((selected) =>
        checkIsTheSameObject({
          compareObjectsBy,
          obj1: suggestion,
          obj2: selected,
        })
      );
    }

    return checkIsTheSameObject({
      compareObjectsBy,
      obj1: suggestion,
      obj2: selectedSuggestion,
    });
  })();

  // is the current element a scrollToSuggestion target
  const isScrollTarget = ((): boolean => {
    if (highlightedSuggestion) return isHighlighted;
    if (isArray(selectedSuggestion)) return false; // do not scroll to selected items in MultiSelect mode
    return isSelected;
  })();

  const key = isObject(suggestion)
    ? JSON.stringify(suggestion)
    : (suggestion as string);

  const item = suggestion === placeholder ? null : suggestion;

  return {
    isHighlighted,
    isPlaceholder,
    isScrollTarget,
    isSelectAllItem,
    isSelected,
    item,
    key,
    selectAllState, // todo: remove
    text,
  };
};
